import React, { useState, useMemo, useEffect } from 'react';
import { Card, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useEntity } from '../../../contexts/EntityContext';
import { useArea } from '../../../contexts/AreaContext';
import { useYear } from '../../../contexts/YearContext';
import FilterComponent, { FilterOption, AppliedFilter } from '../../common/FilterComponent';
import ActionModalComponent from './ActionModalComponent';
import CreateNewRecordModal, { RecordType } from './CreateNewRecordModal';
import { questionService, Questao, QuestaoResponse } from '../../../services/questionService';
import { itemService, ItemResponse } from '../../../services/itemService';
import { actionService, AcaoResponse } from '../../../services/actionService';
import { userService, UsuarioSimplificadoResponse } from '../../../services/userService';
import './QuestionComponent.css';

export type ActionStatus = 'PENDENTE' | 'EM_ANDAMENTO' | 'AGUARDANDO_VALIDACAO' | 'CONCLUIDA' | 'NAO_CONCLUIDA';

export interface Action {
    titulo: string;
    descricao: string;
    acao_base_id: string;
    score: number;
    status: ActionStatus;
    acao_id: string;
    usuario_responsavel_id: string;
    usuario_responsavel_nome: string;
    item_id: string;
    dt_criacao: string;
    responses?: Response[];
}

export interface Item {
    item_id: string;
    titulo: string;
    descricao: string;
    item_base_id: string;
    statusType: string;
    score: number;
    actions: Action[];
    total_acoes: number;
    usuario_responsavel_id?: string;
}

interface QuestionComponentProps {
    title: string;
    onReturn: () => void;
    onExecuteAction?: (action: Action, item: Item, question: Questao) => void;
    moduleType?: string;
}

interface MoveConfirmation {
    action: Action | null;
    fromStatus: ActionStatus | null;
    toStatus: ActionStatus | null;
    questionId: string | null;
    item_id: string | null;
    comment: string;
}

const STATUS_COLUMNS: { id: ActionStatus; title: string; color: string }[] = [
    { id: 'PENDENTE', title: 'Ações Pendentes', color: '#dc3545' },
    { id: 'EM_ANDAMENTO', title: 'Em Andamento', color: '#fd7e14' },
    { id: 'AGUARDANDO_VALIDACAO', title: 'Aguardando Validação', color: '#0dcaf0' },
    { id: 'CONCLUIDA', title: 'Concluída', color: '#198754' },
    { id: 'NAO_CONCLUIDA', title: 'Não Concluída', color: '#6c757d' }
];


const getStatusDisplayText = (status: ActionStatus): string => {
    const statusMap: Record<ActionStatus, string> = {
        'PENDENTE': 'Pendente',
        'EM_ANDAMENTO': 'Em Andamento',
        'AGUARDANDO_VALIDACAO': 'Aguardando Validação',
        'CONCLUIDA': 'Concluída',
        'NAO_CONCLUIDA': 'Não Concluída'
    };
    return statusMap[status] || status;
};

const QuestionComponent: React.FC<QuestionComponentProps> = ({
    title,
    onReturn,
}) => {
    const { currentEntity } = useEntity();
    const { currentArea } = useArea();
    const { currentYear } = useYear();

    const [questions, setQuestions] = useState<Questao[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [itemsLoading, setItemsLoading] = useState(false);
    const [itemsError, setItemsError] = useState<string | null>(null);
    const [itemsByQuestion, setItemsByQuestion] = useState<{ [questionId: string]: Item[] }>({});

    const [actionsLoading, setActionsLoading] = useState(false);
    const [actionsError, setActionsError] = useState<string | null>(null);
    const [actionsByItem, setActionsByItem] = useState<{ [item_id: string]: Action[] }>({});

    const [userPermissions, setUserPermissions] = useState<string[]>([]);
    const [userPermissionsLoading, setUserPermissionsLoading] = useState(false);
    const [userPermissionsError, setUserPermissionsError] = useState<string | null>(null);

    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'question' | 'item' | 'action'>('question');
    const [showFilter, setShowFilter] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

    const [moveConfirmation, setMoveConfirmation] = useState<MoveConfirmation>({
        action: null,
        fromStatus: null,
        toStatus: null,
        questionId: null,
        item_id: null,
        comment: ''
    });
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [selectedAction, setSelectedAction] = useState<Action | null>(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createModalRecordType, setCreateModalRecordType] = useState<RecordType>('question');

    // New state for edit modals
    const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
    const [showEditItemModal, setShowEditItemModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<Questao | null>(null);
    const [editingItem, setEditingItem] = useState<Item | null>(null);
    const [availableUsers, setAvailableUsers] = useState<UsuarioSimplificadoResponse[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    const selectedQuestion = useMemo(() =>
        questions.find(q => q.questao_id === selectedQuestionId),
        [questions, selectedQuestionId]
    );

    const selectedItem = useMemo(() => {
        if (!selectedQuestionId || !selectedItemId) return null;
        const items = itemsByQuestion[selectedQuestionId] || [];
        const item = items.find(i => i.item_id === selectedItemId) || null;

        if (item) {
            const actions = actionsByItem[selectedItemId] || [];
            return {
                ...item,
                actions: actions
            };
        }

        return null;
    }, [selectedQuestionId, selectedItemId, itemsByQuestion, actionsByItem]);

    const filteredQuestions = useMemo(() => {
        // Get the question filter if applied
        const questionFilter = appliedFilters.find(f => f.type === 'question');
        if (!questionFilter) return questions;

        const searchText = questionFilter.value.toLowerCase();
        return questions.filter(question =>
            question.titulo.toLowerCase().includes(searchText)
        );
    }, [questions, appliedFilters]);

    const itemsForSelectedQuestion = useMemo(() => {
        if (!selectedQuestionId) return [];
        const items = itemsByQuestion[selectedQuestionId] || [];

        // Get the item filter if applied
        const itemFilter = appliedFilters.find(f => f.type === 'item');
        if (!itemFilter) return items;

        const searchText = itemFilter.value.toLowerCase();
        return items.filter(item =>
            item.titulo.toLowerCase().includes(searchText)
        );
    }, [selectedQuestionId, itemsByQuestion, appliedFilters]);

    // Update filter type based on current view
    useEffect(() => {
        const newFilterType = selectedQuestionId && selectedItemId ? 'action' :
            selectedQuestionId ? 'item' : 'question';

        // Only clear filter if filter type is actually changing
        if (filterType !== newFilterType) {
            setFilterText('');
            // Clear applied filters when changing view
            setAppliedFilters([]);
        }

        setFilterType(newFilterType);
    }, [selectedQuestionId, selectedItemId, filterType]);

    useEffect(() => {
        const fetchUserPermissions = async () => {
            try {
                setUserPermissionsLoading(true);
                setUserPermissionsError(null);

                if (!currentEntity) {
                    setUserPermissionsError('Entidade não selecionada');
                    return;
                }

                const userInfo = await userService.getUserInfoByEntidade(currentEntity.entidade_id);

                const permissions: string[] = [];
                userInfo.forEach(user => {
                    if (user.permissoes && Array.isArray(user.permissoes)) {
                        permissions.push(...user.permissoes);
                    }
                });

                setUserPermissions(permissions);
            } catch (err) {
                setUserPermissionsError('Erro ao carregar permissões do usuário');
                console.error('Error fetching user permissions:', err);
            } finally {
                setUserPermissionsLoading(false);
            }
        };

        fetchUserPermissions();
    }, [currentEntity]);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!currentEntity || !currentArea || !currentYear) {
                    setError('Entidade, área ou ano não selecionados');
                    return;
                }

                const questionsData = await questionService.getQuestionsForUser(
                    currentEntity.entidade_id,
                    currentArea.area_id,
                    currentYear.ano
                );

                setQuestions(questionsData);
            } catch (err) {
                setError('Erro ao carregar questões. Por favor, tente novamente.');
                console.error('Error fetching questions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [currentEntity, currentArea, currentYear]);

    // Fetch available users when entity changes
    useEffect(() => {
        const fetchAvailableUsers = async () => {
            if (!currentEntity) return;

            try {
                setUsersLoading(true);
                const users = await userService.getActiveUsersByEntity(currentEntity.entidade_id);
                setAvailableUsers(users);
            } catch (error) {
                console.error('Error fetching available users:', error);
            } finally {
                setUsersLoading(false);
            }
        };

        if (currentEntity) {
            fetchAvailableUsers();
        }
    }, [currentEntity]);

    const fetchItemsForQuestion = async (questionId: string) => {
        try {
            setItemsLoading(true);
            setItemsError(null);

            const itemsData = await itemService.getItemsByQuestion(questionId);

            const itemsWithActions: Item[] = itemsData.map((item: ItemResponse) => ({
                ...item,
                descricao: item.descricao, // Use the actual descricao from backend
                actions: [],
                total_acoes: item.total_acoes
            }));

            setItemsByQuestion(prev => {
                const updated = {
                    ...prev,
                    [questionId]: itemsWithActions
                };
                return updated;
            });
        } catch (err) {
            setItemsError('Erro ao carregar itens. Por favor, tente novamente.');
            console.error('Error fetching items:', err);
        } finally {
            setItemsLoading(false);
        }
    };

    const fetchActionsForItem = async (item_id: string) => {
        try {
            setActionsLoading(true);
            setActionsError(null);

            const actionsData = await actionService.getActionsByItem(item_id);

            const actionsWithResponses: Action[] = actionsData.map((action: AcaoResponse) => ({
                acao_id: action.acao_id,
                titulo: action.titulo,
                acao_base_id: action.acao_base_id,
                descricao: action.descricao,
                status: action.status as ActionStatus,
                score: action.score,
                usuario_responsavel_id: action.usuario_responsavel_id,
                usuario_responsavel_nome: action.usuario_responsavel_nome,
                item_id: action.item_id,
                dt_criacao: action.dt_criacao,
                responses: []
            }));

            setActionsByItem(prev => {
                const updated = {
                    ...prev,
                    [item_id]: actionsWithResponses
                };
                return updated;
            });
        } catch (err) {
            setActionsError('Erro ao carregar ações. Por favor, tente novamente.');
            console.error('Error fetching actions:', err);
        } finally {
            setActionsLoading(false);
        }
    };

    const handleQuestionSelect = (questionId: string) => {
        setSelectedQuestionId(questionId);
        setSelectedItemId(null);
        fetchItemsForQuestion(questionId);
    };

    const handleItemSelect = (item_id: string) => {
        setSelectedItemId(item_id);
        fetchActionsForItem(item_id);
    };

    const handleBackToQuestions = () => {
        setSelectedQuestionId(null);
        setSelectedItemId(null);
    };

    const handleBackToItems = () => {
        setSelectedItemId(null);
    };

    const handleOpenCreateModal = (recordType: RecordType) => {
        setCreateModalRecordType(recordType);
        setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
    };

    // New functions for edit modals

    const handleOpenEditQuestionModal = (question: Questao) => {
        setEditingQuestion(question);
        setShowEditQuestionModal(true);
        // Users are already loaded when entity changes
    };

    const handleOpenEditItemModal = (item: Item) => {
        setEditingItem(item);
        setShowEditItemModal(true);
        // Users are already loaded when entity changes
    };

    const handleCloseEditQuestionModal = () => {
        setShowEditQuestionModal(false);
        setEditingQuestion(null);
        setValidationErrors({});
    };

    const handleCloseEditItemModal = () => {
        setShowEditItemModal(false);
        setEditingItem(null);
        setValidationErrors({});
    };

    const handleUpdateQuestion = async (questionId: string, responsavelId: string) => {
        try {
            if (!currentYear) {
                console.error('No year selected');
                return;
            }

            await questionService.updateQuestion(questionId, {
                titulo: editingQuestion?.titulo || '',
                descricao: editingQuestion?.descricao || '',
                ano: currentYear.ano,
                usuarioResponsavelId: responsavelId
            });

            // Update only the specific question in the list to maintain order
            setQuestions(prevQuestions => 
                prevQuestions.map(question => 
                    question.questao_id === questionId 
                        ? { ...question, usuario_responsavel_id: responsavelId }
                        : question
                )
            );

            handleCloseEditQuestionModal();
        } catch (error) {
            console.error('Error updating question:', error);
        }
    };

    const handleUpdateItem = async (itemId: string, responsavelId: string) => {
        try {
            await itemService.updateItem(itemId, {
                titulo: editingItem?.titulo || '',
                descricao: editingItem?.descricao || '', // Now using the actual description field
                usuarioResponsavelId: responsavelId
            });

            // Update only the specific item in the list to maintain order
            if (selectedQuestionId) {
                setItemsByQuestion(prevItems => ({
                    ...prevItems,
                    [selectedQuestionId]: prevItems[selectedQuestionId]?.map(item =>
                        item.item_id === itemId
                            ? { ...item, usuario_responsavel_id: responsavelId }
                            : item
                    ) || []
                }));
            }

            handleCloseEditItemModal();
        } catch (error) {
            console.error('Error updating item:', error);
        }
    };

    // Helper function to get user name by ID
    const getUserNameById = (userId: string): string => {
        const user = availableUsers.find(u => u.usuarioId === userId);
        return user ? `${user.nome} (${user.email})` : userId;
    };

    const handleSaveNewRecord = async (record: QuestaoResponse | Item | Action) => {
        if ('questao_id' in record && (record as any).questao_id) {
            try {
                if (currentEntity && currentArea && currentYear) {

                    const questionsData = await questionService.getQuestionsForUser(
                        currentEntity.entidade_id,
                        currentArea.area_id,
                        currentYear.ano
                    );

                    setQuestions(questionsData);
                }
            } catch (error) {
                console.error('Error refreshing questions after creation:', error);
                setError('Erro ao atualizar lista de questões. Por favor, recarregue a página.');
            }
        }

        if ('item_id' in record && record.item_id && !record.item_id.startsWith('new-item-')) {
            try {
                if (selectedQuestionId) {
                    const itemsData = await itemService.getItemsByQuestion(selectedQuestionId);

                    const transformedItems: Item[] = itemsData.map((item: ItemResponse) => ({
                        item_id: item.item_id,
                        titulo: item.titulo,
                        descricao: item.descricao, // Use the actual descricao from backend
                        item_base_id: item.item_base_id,
                        statusType: item.statusType,
                        score: item.score,
                        actions: [],
                        total_acoes: item.total_acoes
                    }));

                    setItemsByQuestion(prev => ({
                        ...prev,
                        [selectedQuestionId]: transformedItems
                    }));
                }
            } catch (error) {
                console.error('Error refreshing items after creation:', error);
                setItemsError('Erro ao atualizar lista de itens. Por favor, recarregue a página.');
            }
        }

        if ('acao_id' in record && record.acao_id && !record.acao_id.startsWith('new-action-')) {
            try {
                if (selectedItemId) {
                    const actionsData = await actionService.getActionsByItem(selectedItemId);
                    const actionsWithResponses: Action[] = actionsData.map((action: AcaoResponse) => ({
                        ...action,
                        status: action.status as ActionStatus,
                        responses: []
                    }));

                    setActionsByItem(prev => ({
                        ...prev,
                        [selectedItemId]: actionsWithResponses
                    }));
                }
            } catch (error) {
                console.error('Error refreshing actions after creation:', error);
                setActionsError('Erro ao atualizar lista de ações. Por favor, recarregue a página.');
            }
        }
        handleCloseCreateModal();
    };


    const handleDragEnd = (result: any, questionId: string, item_id: string) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const fromStatus = source.droppableId as ActionStatus;
        const toStatus = destination.droppableId as ActionStatus;

        if (fromStatus === toStatus) {
            setWarningMessage('Não é possível mover uma ação para o mesmo status.');
            setShowWarningModal(true);
            return;
        }

        const sourceActions = getActionsByStatus(selectedItem?.actions || [], fromStatus);

        const actionToMove = sourceActions[source.index];

        if (!actionToMove) return;

        // Check if the action is in a final state (cannot be moved)
        if (fromStatus === 'CONCLUIDA' || fromStatus === 'NAO_CONCLUIDA') {
            setWarningMessage('Ações concluídas ou não concluídas não podem ser movidas.');
            setShowWarningModal(true);
            return;
        }

        // Define allowed movements based on current status
        let allowedMovements: ActionStatus[] = [];

        switch (fromStatus) {
            case 'PENDENTE':
                // PENDENTE can only move to EM_ANDAMENTO (next stage)
                allowedMovements = ['EM_ANDAMENTO'];
                break;
            case 'EM_ANDAMENTO':
                // EM_ANDAMENTO can move to AGUARDANDO_VALIDACAO, CONCLUIDA, or NAO_CONCLUIDA
                allowedMovements = ['AGUARDANDO_VALIDACAO', 'CONCLUIDA', 'NAO_CONCLUIDA'];
                break;
            case 'AGUARDANDO_VALIDACAO':
                // AGUARDANDO_VALIDACAO can move to CONCLUIDA or NAO_CONCLUIDA
                allowedMovements = ['CONCLUIDA', 'NAO_CONCLUIDA'];
                break;
            default:
                setWarningMessage('Movimentação não permitida para este status.');
                setShowWarningModal(true);
                return;
        }

        // Check if the target status is allowed
        if (!allowedMovements.includes(toStatus)) {
            setWarningMessage(`Não é possível mover uma ação de "${getStatusDisplayText(fromStatus)}" para "${getStatusDisplayText(toStatus)}".`);
            setShowWarningModal(true);
            return;
        }

        setMoveConfirmation({
            action: actionToMove,
            fromStatus,
            toStatus,
            questionId,
            item_id,
            comment: ''
        });
    };

    const getActionsByStatus = (actions: Action[], status: ActionStatus) => {
        return actions.filter(action => action.status === status);
    };

    const handleConfirmMove = async () => {
        const { action, fromStatus, toStatus, questionId, item_id, comment } = moveConfirmation;

        if (!action || !fromStatus || !toStatus || !questionId || !item_id) return;

        try {
            const updatedAction = await actionService.moveAction(
                action.acao_id,
                toStatus,
                comment || undefined,
                undefined // No files for status change
            );

            if (toStatus === 'CONCLUIDA') {
                if (currentEntity && currentArea && currentYear) {
                    try {
                        const questionsData = await questionService.getQuestionsForUser(
                            currentEntity.entidade_id,
                            currentArea.area_id,
                            currentYear.ano
                        );
                        setQuestions(questionsData);
                    } catch (error) {
                        console.error('Error fetching updated questions:', error);
                    }
                }

                try {
                    const itemsData = await itemService.getItemsByQuestion(questionId);
                    const itemsWithActions: Item[] = itemsData.map((item: any) => ({
                        ...item,
                        actions: []
                    }));

                    setItemsByQuestion(prev => ({
                        ...prev,
                        [questionId]: itemsWithActions
                    }));
                } catch (error) {
                    console.error('Error fetching updated items:', error);
                }

                try {
                    const actionsData = await actionService.getActionsByItem(item_id);
                    const actionsWithResponses: Action[] = actionsData.map((action: any) => ({
                        ...action,
                        status: action.status as ActionStatus,
                        responses: []
                    }));

                    setActionsByItem(prev => ({
                        ...prev,
                        [item_id]: actionsWithResponses
                    }));
                } catch (error) {
                    console.error('Error fetching updated actions:', error);
                }
            } else {
                setItemsByQuestion(prev => {
                    const questionItems = prev[questionId] || [];
                    const updatedItems = questionItems.map(item => {
                        if (item.item_id === item_id) {
                            const actions = [...item.actions];
                            const actionToUpdate = actions.find(a => a.acao_id === action.acao_id);

                            if (actionToUpdate) {
                                actionToUpdate.status = updatedAction.status as ActionStatus;
                                actionToUpdate.score = updatedAction.score;
                            }

                            return {
                                ...item,
                                actions
                            };
                        }
                        return item;
                    });

                    return {
                        ...prev,
                        [questionId]: updatedItems
                    };
                });

                setActionsByItem(prev => {
                    const itemActions = prev[item_id] || [];
                    const updatedActions = itemActions.map(a => {
                        if (a.acao_id === action.acao_id) {
                            return {
                                ...a,
                                status: updatedAction.status as ActionStatus,
                                score: updatedAction.score
                            };
                        }
                        return a;
                    });

                    return {
                        ...prev,
                        [item_id]: updatedActions
                    };
                });
            }

            setMoveConfirmation({
                action: null,
                fromStatus: null,
                toStatus: null,
                questionId: null,
                item_id: null,
                comment: ''
            });
        } catch (error) {
            console.error('Error moving action:', error);
            setError('Erro ao mover ação. Por favor, tente novamente.');
        }
    };

    const handleCancelMove = () => {
        setMoveConfirmation({
            action: null,
            fromStatus: null,
            toStatus: null,
            questionId: null,
            item_id: null,
            comment: ''
        });
    };

    const handleActionCardClick = (action: Action) => {
        setSelectedAction(action);
        setShowActionModal(true);
    };

    const handleCloseActionModal = () => {
        setShowActionModal(false);
        setSelectedAction(null);
    };

    const handleActionUpdate = (updatedAction: Action) => {
        setSelectedAction(updatedAction);

        setActionsByItem(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(itemId => {
                updated[itemId] = updated[itemId].map(action =>
                    action.acao_id === updatedAction.acao_id ? updatedAction : action
                );
            });
            return updated;
        });

        setItemsByQuestion(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(questionId => {
                updated[questionId] = updated[questionId].map(item => ({
                    ...item,
                    actions: item.actions.map(action =>
                        action.acao_id === updatedAction.acao_id ? updatedAction : action
                    )
                }));
            });
            return updated;
        });
    };

    const handleSaveComment = (comment: string, files: File[]) => {
        if (!selectedAction) return;
    };

    const getActionHistory = async (actionId: string) => {
        try {
            const historyData = await actionService.getActionHistory(actionId);
            return historyData;
        } catch (error) {
            console.error('Error fetching action history:', error);
            return [];
        }
    };

    const handleFilterApply = (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'question' | 'item' | 'action';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterText.trim(),
            label: filterOption?.label || typedFilterType
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');
    };

    const handleFilterRemove = (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
    };

    const handleFilterClear = () => {
        setFilterText('');
        setAppliedFilters([]);
    };

    const hasUserPermission = (permission: string): boolean => {
        return userPermissions.includes(permission);
    };

    const filterOptions: FilterOption[] = useMemo(() => {
        if (selectedQuestionId && selectedItemId) {
            // User is viewing actions (kanban board)
            return [{ value: 'action', label: 'Ação' }];
        } else if (selectedQuestionId) {
            // User is viewing items
            return [{ value: 'item', label: 'Item' }];
        } else {
            // User is viewing questions
            return [{ value: 'question', label: 'Questão' }];
        }
    }, [selectedQuestionId, selectedItemId]);

    if (loading || userPermissionsLoading) {
        return (
            <div className="questions-component p-4">
                <Button
                    variant="outline-secondary"
                    onClick={onReturn}
                    className="return-button mb-3 d-flex align-items-center gap-2"
                >
                    <span>←</span> Voltar
                </Button>
                <div className="text-center p-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">
                        {loading ? 'Carregando questões...' : 'Carregando permissões do usuário...'}
                    </p>
                </div>
            </div>
        );
    }

    if (error || userPermissionsError) {
        return (
            <div className="questions-component p-4">
                <Button
                    variant="outline-secondary"
                    onClick={onReturn}
                    className="return-button mb-3 d-flex align-items-center gap-2"
                >
                    <span>←</span> Voltar
                </Button>
                <Alert variant="danger" onClose={() => {
                    setError(null);
                    setUserPermissionsError(null);
                }} dismissible>
                    {error || userPermissionsError}
                </Alert>
            </div>
        );
    }

    return (
        <div className="questions-component p-4">
            <Button
                variant="outline-secondary"
                onClick={onReturn}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>

            {userPermissionsError && (
                <Alert variant="warning" onClose={() => setUserPermissionsError(null)} dismissible className="mb-3">
                    <strong>Atenção:</strong> Não foi possível carregar as permissões do usuário. Algumas funcionalidades podem estar limitadas.
                </Alert>
            )}

            <div className="dashboard-header mb-4 d-flex flex-row justify-content-between align-items-center">
                <h2 className="mb-0 text-start">{title}</h2>
                {!selectedQuestionId && hasUserPermission('questao.create') && (
                    <Button
                        variant="primary"
                        className="shadow-sm d-flex align-items-center"
                        onClick={() => handleOpenCreateModal('question')}
                    >
                        <i className="mdi mdi-plus me-2"></i>
                        Adicionar Questão
                    </Button>
                )}
                {!selectedItemId && selectedQuestionId && hasUserPermission('item.create') && (
                    <Button
                        variant="primary"
                        className="shadow-sm d-flex align-items-center"
                        onClick={() => handleOpenCreateModal('item')}
                    >
                        <i className="mdi mdi-plus me-2"></i>
                        Adicionar Item
                    </Button>
                )}
                {!selectedAction && selectedItemId && selectedQuestionId && hasUserPermission('acao.create') && (
                    <Button
                        variant="primary"
                        className="shadow-sm d-flex align-items-center"
                        onClick={() => handleOpenCreateModal('action')}
                    >
                        <i className="mdi mdi-plus me-2"></i>
                        Adicionar Ação
                    </Button>
                )}
            </div>

            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span>🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        onFilterApply={handleFilterApply}
                        onFilterTypeChange={(type) => setFilterType(type as 'question' | 'item' | 'action')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={`Buscar por ${filterType === 'question' ? 'questão' : filterType === 'item' ? 'item' : 'ação'}...`}
                    />
                </div>
            )}

            <div className="navigation-breadcrumb mb-3">
                {selectedQuestion && (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="link"
                            className="p-0 text-decoration-none text-secondary"
                            onClick={handleBackToQuestions}
                        >
                            Questões
                        </Button>
                        <span className="text-muted">/</span>
                        {selectedItem ? (
                            <>
                                <Button
                                    variant="link"
                                    className="p-0 text-decoration-none text-secondary"
                                    onClick={handleBackToItems}
                                >
                                    {selectedQuestion.titulo}
                                </Button>
                                <span className="text-muted">/</span>
                                <span className="text-muted">{selectedItem.titulo}</span>
                            </>
                        ) : (
                            <span className="text-muted">{selectedQuestion.titulo}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Questions List */}
            {!selectedQuestionId && (
                <div className="questions-list">
                    {filteredQuestions.map((question) => (
                        <div key={question.questao_id} className="position-relative">
                            <Card className="mb-3 question-card clickable-card" onClick={() => {
                                const questionId = question.questao_id || (question as any).id || (question as any).questao_id;
                                handleQuestionSelect(questionId);
                            }}>
                                <Card.Body className="py-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        {/* Ícone + Título */}
                                        <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: '300px' }}>
                                            <div className="d-flex align-items-center me-3">
                                                {!question.questao_base_id ? (
                                                    <i
                                                        className="mdi mdi-account-edit me-2 text-secondary"
                                                        style={{ fontSize: '1.5rem' }}
                                                        title="Esta questão foi criada pelos administradores da entidade"
                                                    ></i>
                                                ) : (
                                                    <i
                                                        className="mdi mdi-file-document me-2 text-danger"
                                                        style={{ fontSize: '1.5rem' }}
                                                        title="Esta questão foi criada pelo sistema"
                                                    ></i>
                                                )}
                                                <h6 className="mb-0 text-truncate">
                                                    {question.titulo}
                                                </h6>
                                            </div>
                                        </div>

                                        {/* Quantidade de Itens */}
                                        <div className="d-flex align-items-center me-3" style={{ minWidth: '100px' }}>
                                            <small className="text-muted">
                                                {question.total_items} {question.total_items === 1 || question.total_items === 0 ? 'item' : 'itens'}
                                            </small>
                                        </div>

                                        {/* Responsável */}
                                        <div className="d-flex align-items-center me-3" style={{ minWidth: '300px' }}>
                                            <small className="text-muted d-flex align-items-center">
                                                <i className="mdi mdi-account me-1"></i>
                                                <span style={{ maxWidth: '300px' }}>
                                                    {question.usuario_responsavel_id ?
                                                        getUserNameById(question.usuario_responsavel_id) :
                                                        'Sem responsável'
                                                    }
                                                </span>
                                            </small>
                                        </div>

                                        {/* Nota */}
                                        <div className="d-flex align-items-center me-3" style={{ minWidth: '80px' }}>
                                            <div className="d-flex flex-column align-items-center">
                                                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#212529' }}>
                                                    {question.score || 0}
                                                </span>
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Nota Atual</small>
                                            </div>
                                        </div>

                                        {/* Ações */}
                                        <div className="d-flex align-items-center">
                                            {hasUserPermission('questao.edit') && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEditQuestionModal(question);
                                                    }}
                                                    className="p-1 edit-button me-2"
                                                    title="Editar Questão"
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    <i className="mdi mdi-pencil"></i>
                                                </Button>
                                            )}
                                            <i className="mdi mdi-chevron-right text-primary click-indicator" style={{ fontSize: '1.2rem' }} title="Clique para ver itens"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ))}
                </div>
            )}

            {/* Items List */}
            {selectedQuestionId && !selectedItemId && (
                <div className="items-list">
                    {itemsLoading && (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status" size="sm">
                                <span className="visually-hidden">Carregando...</span>
                            </Spinner>
                            <p className="mt-2 text-muted">Carregando itens...</p>
                        </div>
                    )}

                    {itemsError && !itemsLoading && (
                        <Alert variant="danger" onClose={() => setItemsError(null)} dismissible>
                            {itemsError}
                        </Alert>
                    )}

                    {!itemsLoading && !itemsError && itemsForSelectedQuestion.map((item) => (
                        <div key={item.item_id} className="position-relative">
                            <Card className="mb-3 item-card clickable-card" onClick={() => handleItemSelect(item.item_id)}>
                                <Card.Body className="py-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        {/* Ícone + Título */}
                                        <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: '300px' }}>
                                            <div className="d-flex align-items-center me-3">
                                                {!item.item_base_id ? (
                                                    <i
                                                        className="mdi mdi-account-edit me-2 text-secondary"
                                                        style={{ fontSize: '1.5rem' }}
                                                        title="Este item foi criado pelos administradores da entidade"
                                                    ></i>
                                                ) : (
                                                    <i
                                                        className="mdi mdi-file-document me-2 text-danger"
                                                        style={{ fontSize: '1.5rem' }}
                                                        title="Este item foi criado pelo sistema"
                                                    ></i>
                                                )}
                                                <h6 className="mb-0 text-truncate">
                                                    {item.titulo}
                                                </h6>
                                            </div>
                                        </div>

                                        {/* Quantidade de Ações */}
                                        <div className="d-flex align-items-center me-3" style={{ minWidth: '100px' }}>
                                            <small className="text-muted">
                                                {item.total_acoes} {item.total_acoes === 1 || item.total_acoes === 0 ? 'ação' : 'ações'}
                                            </small>
                                        </div>

                                        {/* Responsável */}
                                        <div className="d-flex align-items-center me-3" style={{ minWidth: '300px' }}>
                                            <small className="text-muted d-flex align-items-center">
                                                <i className="mdi mdi-account me-1"></i>
                                                <span style={{ maxWidth: '300px' }}>
                                                    {item.usuario_responsavel_id ?
                                                        getUserNameById(item.usuario_responsavel_id) :
                                                        'Sem responsável'
                                                    }
                                                </span>
                                            </small>
                                        </div>

                                        {/* Nota */}
                                        <div className="d-flex align-items-center me-3" style={{ minWidth: '80px' }}>
                                            <div className="d-flex flex-column align-items-center">
                                                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#212529' }}>
                                                    {item.score || 0}
                                                </span>
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Nota Atual</small>
                                            </div>
                                        </div>

                                        {/* Ações */}
                                        <div className="d-flex align-items-center">
                                            {hasUserPermission('item.edit') && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEditItemModal(item);
                                                    }}
                                                    className="p-1 edit-button me-2"
                                                    title="Editar Item"
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    <i className="mdi mdi-pencil"></i>
                                                </Button>
                                            )}
                                            <i className="mdi mdi-chevron-right text-primary click-indicator" style={{ fontSize: '1.2rem' }} title="Clique para ver ações"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ))}
                </div>
            )}

            {/* Kanban Board */}
            {selectedQuestionId && selectedItemId && selectedQuestion && selectedItem && (
                <div className="kanban-container">
                    {actionsLoading && (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status" size="sm">
                                <span className="visually-hidden">Carregando...</span>
                            </Spinner>
                            <p className="mt-2 text-muted">Carregando ações...</p>
                        </div>
                    )}

                    {actionsError && !actionsLoading && (
                        <Alert variant="danger" onClose={() => setActionsError(null)} dismissible>
                            {actionsError}
                        </Alert>
                    )}

                    {!actionsLoading && !actionsError && (
                        <DragDropContext onDragEnd={(result) => handleDragEnd(result, selectedQuestion.questao_id, selectedItem.item_id)}>
                            <div className="action-board">
                                <div className="kanban-header">
                                    <div className="d-flex flex-column gap-2">
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-2">Questão:</span>
                                            <span className="kanban-title">{selectedQuestion.titulo}</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-2">Item:</span>
                                            <span className="kanban-title">{selectedItem.titulo}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="action-columns">
                                    {STATUS_COLUMNS.map((column) => {
                                        const allActions = getActionsByStatus(selectedItem.actions, column.id);

                                        let displayActions = allActions;

                                        // Get the action filter if applied
                                        const actionFilter = appliedFilters.find(f => f.type === 'action');
                                        if (actionFilter) {
                                            const searchText = actionFilter.value.toLowerCase();
                                            displayActions = allActions.filter(action =>
                                                action.descricao.toLowerCase().includes(searchText) ||
                                                (action.titulo && action.titulo.toLowerCase().includes(searchText))
                                            );
                                        }

                                        const indexMap = new Map(
                                            displayActions.map((action, filteredIndex) => [
                                                filteredIndex,
                                                allActions.findIndex(a => a.acao_id === action.acao_id)
                                            ])
                                        );

                                        return (
                                            <div key={column.id} className="action-column">
                                                <div
                                                    className="column-header"
                                                    style={{ borderColor: column.color }}
                                                >
                                                    <h6>{column.title}</h6>
                                                    <span className="action-count">
                                                        {displayActions.length}
                                                    </span>
                                                </div>
                                                <Droppable droppableId={column.id}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                            className={`action-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                                                            style={{
                                                                backgroundColor: snapshot.isDraggingOver ? '#e9ecef' : undefined,
                                                                opacity: snapshot.isDraggingOver ? 0.8 : 1
                                                            }}
                                                        >
                                                            {displayActions.map((action, filteredIndex) => {
                                                                const originalIndex = indexMap.get(filteredIndex) ?? filteredIndex;

                                                                return (
                                                                    <Draggable
                                                                        key={`${action.acao_id}-${column.id}`}
                                                                        draggableId={`${action.acao_id}-${column.id}`}
                                                                        index={originalIndex}
                                                                        isDragDisabled={action.status === 'CONCLUIDA' || action.status === 'NAO_CONCLUIDA'}
                                                                    >
                                                                        {(provided, snapshot) => (
                                                                            <div className="position-relative">
                                                                                <div
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    className={`action-card ${snapshot.isDragging ? 'dragging' : ''}`}
                                                                                    style={{
                                                                                        ...provided.draggableProps.style,
                                                                                        opacity: snapshot.isDragging ? 0.8 : 1,
                                                                                        cursor: action.status === 'CONCLUIDA' || action.status === 'NAO_CONCLUIDA' ? 'not-allowed' : 'pointer',
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center',
                                                                                    }}
                                                                                    onClick={() => handleActionCardClick(action)}
                                                                                >
                                                                                    <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                                                                                        {/* <h6 className="mb-2 text-truncate">{action.titulo}</h6> */}
                                                                                        {!action.acao_base_id ? (
                                                                                            <i
                                                                                                className="mdi mdi-account-edit me-2 text-secondary"
                                                                                                style={{ fontSize: '1.5rem' }}
                                                                                                title="Esta ação foi criada pelos administradores da entidade"
                                                                                            ></i>
                                                                                        ) : (
                                                                                            <i
                                                                                                className="mdi mdi-file-document me-2 text-danger"
                                                                                                style={{ fontSize: '1.5rem' }}
                                                                                                title="Esta ação foi criada pelo sistema"
                                                                                            ></i>
                                                                                        )}
                                                                                        <p className="mb-0 text-muted" style={{ wordWrap: 'break-word' }}>{action.titulo}</p>
                                                                                    </div>
                                                                                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minWidth: 80 }}>
                                                                                        <span className="text-center" style={{ fontSize: '1.15rem', fontWeight: 700, background: 'none', color: '#212529', padding: 0 }}>{action.score || 0}</span>
                                                                                        <small className="text-muted text-center" style={{ fontSize: '0.75rem' }}>Nota Atual</small>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </DragDropContext>
                    )}
                </div>
            )}

            {/* Warning Modal */}
            <Modal
                show={showWarningModal}
                onHide={() => setShowWarningModal(false)}
                centered
            >
                <Modal.Header>
                    <Modal.Title>Atenção</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-4 me-3"></i>
                        <p className="mb-0">{warningMessage}</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowWarningModal(false)}
                        className="px-4"
                    >
                        Entendi
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Move Confirmation Modal */}
            <Modal
                show={!!moveConfirmation.action}
                onHide={handleCancelMove}
                centered
            >
                <Modal.Header>
                    <Modal.Title>Confirmar Movimentação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {moveConfirmation.action && (
                        <>
                            <p className="mb-3">
                                Você está movendo a ação <strong>{moveConfirmation.action.descricao}</strong> de
                                <br />
                                <span className="status-badge ms-2 me-2" style={{
                                    backgroundColor: STATUS_COLUMNS.find(col => col.id === moveConfirmation.fromStatus)?.color
                                }}>
                                    {moveConfirmation.fromStatus ? getStatusDisplayText(moveConfirmation.fromStatus) : ''}
                                </span>
                                para
                                <span className="status-badge ms-2" style={{
                                    backgroundColor: STATUS_COLUMNS.find(col => col.id === moveConfirmation.toStatus)?.color
                                }}>
                                    {moveConfirmation.toStatus ? getStatusDisplayText(moveConfirmation.toStatus) : ''}
                                </span>
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Comentário</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Adicione um comentário sobre esta movimentação..."
                                    value={moveConfirmation.comment}
                                    onChange={(e) => setMoveConfirmation(prev => ({
                                        ...prev,
                                        comment: e.target.value
                                    }))}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelMove}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleConfirmMove}>
                        Confirmar Movimentação
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Action Modal Component */}
            <ActionModalComponent
                action={selectedAction}
                isOpen={showActionModal}
                onClose={handleCloseActionModal}
                onSaveComment={handleSaveComment}
                getActionHistory={getActionHistory}
                userPermissions={userPermissions}
                onActionUpdate={handleActionUpdate}
            />

            {/* Create New Record Modal */}
            <CreateNewRecordModal
                show={showCreateModal}
                onHide={handleCloseCreateModal}
                recordType={createModalRecordType}
                parentQuestion={selectedQuestion ? {
                    questaoId: selectedQuestion.questao_id,
                    titulo: selectedQuestion.titulo,
                    descricao: selectedQuestion.descricao,
                    areaId: selectedQuestion.area_id,
                    itemIds: []
                } : undefined}
                parentItem={selectedItem || undefined}
                onSave={handleSaveNewRecord}
            />

            {/* Edit Question Modal */}
            <Modal
                show={showEditQuestionModal}
                onHide={handleCloseEditQuestionModal}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Editar Questão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {usersLoading ? (
                        <div className="text-center p-3">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 text-muted">Carregando usuários...</p>
                        </div>
                    ) : (
                        <Form>
                            <div className="mb-3">
                                <h6 className="text-muted mb-2">Questão</h6>
                                <div className="p-3 bg-light rounded">
                                    <h6 className="mb-1">{editingQuestion?.titulo}</h6>
                                    <p className="mb-0 text-muted small">{editingQuestion?.descricao}</p>
                                </div>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Responsável</Form.Label>
                                <Form.Select
                                    value={editingQuestion?.usuario_responsavel_id || ''}
                                    onChange={(e) => {
                                        setEditingQuestion(prev => prev ? { ...prev, usuario_responsavel_id: e.target.value } : null);
                                        if (validationErrors.responsavel) {
                                            setValidationErrors(prev => ({ ...prev, responsavel: '' }));
                                        }
                                    }}
                                >
                                    <option value="">Selecione um responsável</option>
                                    {availableUsers.map(user => (
                                        <option key={user.usuarioId} value={user.usuarioId}>
                                            {user.nome} ({user.email})
                                        </option>
                                    ))}
                                </Form.Select>
                                {validationErrors.responsavel && (
                                    <Form.Text className="text-danger">{validationErrors.responsavel}</Form.Text>
                                )}
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditQuestionModal}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            const errors: { [key: string]: string } = {};
                            if (!editingQuestion?.usuario_responsavel_id) errors.responsavel = 'O responsável é obrigatório.';

                            setValidationErrors(errors);

                            if (Object.keys(errors).length === 0 && editingQuestion?.questao_id && editingQuestion?.usuario_responsavel_id) {
                                handleUpdateQuestion(editingQuestion.questao_id, editingQuestion.usuario_responsavel_id);
                            }
                        }}
                        disabled={usersLoading || Object.keys(validationErrors).length > 0}
                    >
                        Salvar Alterações
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Item Modal */}
            <Modal
                show={showEditItemModal}
                onHide={handleCloseEditItemModal}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Editar Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {usersLoading ? (
                        <div className="text-center p-3">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 text-muted">Carregando usuários...</p>
                        </div>
                    ) : (
                        <Form>
                            <div className="mb-3">
                                <h6 className="text-muted mb-2">Item</h6>
                                <div className="p-3 bg-light rounded">
                                    <h6 className="mb-1">{editingItem?.titulo}</h6>
                                    <p className="mb-0 text-muted small">{editingItem?.descricao}</p>
                                </div>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Responsável</Form.Label>
                                <Form.Select
                                    value={editingItem?.usuario_responsavel_id || ''}
                                    onChange={(e) => {
                                        setEditingItem(prev => prev ? { ...prev, usuario_responsavel_id: e.target.value } : null);
                                        if (validationErrors.responsavel) {
                                            setValidationErrors(prev => ({ ...prev, responsavel: '' }));
                                        }
                                    }}
                                >
                                    <option value="">Selecione um responsável</option>
                                    {availableUsers.map(user => (
                                        <option key={user.usuarioId} value={user.usuarioId}>
                                            {user.nome} ({user.email})
                                        </option>
                                    ))}
                                </Form.Select>
                                {validationErrors.responsavel && (
                                    <Form.Text className="text-danger">{validationErrors.responsavel}</Form.Text>
                                )}
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditItemModal}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            const errors: { [key: string]: string } = {};
                            if (!editingItem?.usuario_responsavel_id) errors.responsavel = 'O responsável é obrigatório.';

                            setValidationErrors(errors);

                            if (Object.keys(errors).length === 0 && editingItem?.item_id && editingItem?.usuario_responsavel_id) {
                                handleUpdateItem(editingItem.item_id, editingItem.usuario_responsavel_id);
                            }
                        }}
                        disabled={usersLoading || Object.keys(validationErrors).length > 0}
                    >
                        Salvar Alterações
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default QuestionComponent;
